import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TokenSymbolEnum } from '@/utils/tokens'
import { zeroAddress } from 'viem'
import { MarketEntryWithLaunchDate } from '../types'

const defaultToken = {
  address: CheckedAddress(zeroAddress),
  decimals: 18,
  unitPriceUsd: '0',
}

const defaultEntry: Omit<MarketEntryWithLaunchDate, 'launchDate' | 'token'> = {
  reserveStatus: 'active',
  totalSupplied: NormalizedUnitNumber(0),
  depositAPYDetails: {
    apy: Percentage(0),
    incentives: [],
    airdrops: [],
  },
  totalBorrowed: NormalizedUnitNumber(0),
  borrowAPYDetails: {
    apy: Percentage(0),
    incentives: [],
    airdrops: [],
  },
  marketStatus: {
    supplyAvailabilityStatus: 'no',
    collateralEligibilityStatus: 'no',
    borrowEligibilityStatus: 'no',
  },
  eModeCategoryId: 0,
  maxLtv: Percentage(0),
  userDeposit: NormalizedUnitNumber(0),
}

export const exampleEntry: MarketEntryWithLaunchDate = {
  ...defaultEntry,
  launchDate: 'July 15th',
  token: new Token({
    ...defaultToken,
    symbol: TokenSymbol('EXAMPLE'),
    name: 'Example entry',
  }),
}

export const comingSoonEntries: MarketEntryWithLaunchDate[] = []

export const newEntries: string[] = []

export const topMarkets: string[] = [
  TokenSymbolEnum.HYPE,
  TokenSymbolEnum.USD_T0,
  TokenSymbolEnum.THBILL,
  TokenSymbolEnum.KHYPE,
  TokenSymbolEnum.USDXL,
  TokenSymbolEnum.XAUT0,
]

export function sortMarketEntries<T extends { token: { symbol: string } }>(entries: T[]): T[] {
  return [...entries].sort((a, b) => {
    const aNewIndex = newEntries.indexOf(a.token.symbol)
    const bNewIndex = newEntries.indexOf(b.token.symbol)
    const aTopIndex = topMarkets.indexOf(a.token.symbol)
    const bTopIndex = topMarkets.indexOf(b.token.symbol)

    // First priority: newEntries
    if (aNewIndex !== -1 && bNewIndex !== -1) {
      return aNewIndex - bNewIndex // Both are new, sort by their order in newEntries
    }
    if (aNewIndex !== -1) {
      return -1 // a is new, b is not
    }
    if (bNewIndex !== -1) {
      return 1 // b is new, a is not
    }

    // Second priority: topMarkets (under newEntries but above the rest)
    if (aTopIndex !== -1 && bTopIndex !== -1) {
      return aTopIndex - bTopIndex // Both are top markets, sort by their order in topMarkets
    }
    if (aTopIndex !== -1) {
      return -1 // a is top market, b is not
    }
    if (bTopIndex !== -1) {
      return 1 // b is top market, a is not
    }

    // Neither are new or top markets, maintain original order
    return 0
  })
}

export const emodeOnlyEntries = [
  TokenSymbol(TokenSymbolEnum.PTKHYPE13NOV2025),
  TokenSymbol(TokenSymbolEnum.PTKHYPE19MAR2026),
]
